import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export interface SiteInfo {
  id: string
  name: string
  role: string
}

interface SiteContextValue {
  sites: SiteInfo[]
  currentSite: SiteInfo | undefined
  switchSite: (siteId: string) => void
  addSite: (site: SiteInfo) => void
}

const SiteContext = createContext<SiteContextValue | undefined>(undefined)

const STORAGE_KEY = 'currentSiteId'

interface SiteProviderProps extends PropsWithChildren {
  sites: SiteInfo[]
}

export function SiteProvider({ sites: initialSites, children }: SiteProviderProps) {
  const [sites, setSites] = useState<SiteInfo[]>(initialSites)
  const [currentSiteId, setCurrentSiteId] = useState<string>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && initialSites.some((s) => s.id === stored)) {
      return stored
    }
    return initialSites[0]?.id ?? ''
  })

  const currentSite = useMemo(
    () => sites.find((s) => s.id === currentSiteId),
    [sites, currentSiteId],
  )

  const switchSite = useCallback(
    (siteId: string) => {
      if (sites.some((s) => s.id === siteId)) {
        setCurrentSiteId(siteId)
        localStorage.setItem(STORAGE_KEY, siteId)
      }
    },
    [sites],
  )

  const addSite = useCallback((site: SiteInfo) => {
    setSites((prev) => {
      const isFirst = prev.length === 0
      if (isFirst) {
        setCurrentSiteId(site.id)
        localStorage.setItem(STORAGE_KEY, site.id)
      }
      return [...prev, site]
    })
  }, [])

  const value = useMemo(
    () => ({ sites, currentSite, switchSite, addSite }),
    [sites, currentSite, switchSite, addSite],
  )

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
}

export function useSiteContext(): SiteContextValue {
  const context = useContext(SiteContext)
  if (!context) {
    throw new Error('useSiteContext must be used within a SiteProvider')
  }
  return context
}
